import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, intf, inrFull, statusColor, COLORS, formatTime } from "@/lib/format";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, AreaChart, Area
} from "recharts";
import { Flame, Activity, AlertCircle, ChevronRight, Sparkles, ArrowDown, ArrowUp } from "lucide-react";

// Custom semicircle gauge for SEC vs theoretical / baseline / threshold
// Zones (kWh/t):
//   244        — Theoretical minimum (grey iron physics floor)
//   244 – 480  — Aspirational (between physics floor and world best practice)
//   480 – 540  — World Best Practice
//   540 – 600  — Approaching Indian best
//   600 – 700  — Good Indian Foundry
//   700 – 1000 — Above good practice (drift / improvement zone)
//   1000+      — Poor (urgent action)
function EfficiencyGauge({ actual, theoretical, baseline, threshold, status }) {
  const min = theoretical;          // 244
  const max = 1200;                  // fixed so the ">1000 Poor" band is always visible
  const span = max - min;
  const valToAngle = (v) => -90 + Math.max(0, Math.min(1, (v - min) / span)) * 180;
  const norm = Math.max(0, Math.min(1, (actual - min) / span));
  const angle = -90 + norm * 180;
  const baseAngle = valToAngle(baseline);

  const polar = (deg, r) => {
    const rad = (deg - 90) * Math.PI / 180;
    return { x: 200 + r * Math.cos(rad), y: 200 + r * Math.sin(rad) };
  };

  const arc = (startDeg, endDeg, r) => {
    const start = polar(startDeg, r);
    const end = polar(endDeg, r);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
  };

  const ZONES = [
    { from: 244,  to: 480,  color: "#B7E9F2", label: "Aspirational" },
    { from: 480,  to: 540,  color: "#22C5E5", label: "World best practice" },
    { from: 540,  to: 600,  color: "#0EA5C8", label: "Approaching" },
    { from: 600,  to: 700,  color: "#00FF66", label: "Good Indian foundry" },
    { from: 700,  to: 1000, color: "#FFCC00", label: "Drift" },
    { from: 1000, to: 1200, color: "#FF3333", label: "Poor — urgent" },
  ];

  const needle = polar(angle, 150);
  const color = statusColor(status);

  return (
    <svg viewBox="0 0 400 240" className="w-full h-full" data-testid="furnace-efficiency-gauge">
      {/* Dark backing arc */}
      <path d={arc(0, 180, 165)} stroke="#1F212A" strokeWidth="2" fill="none" />
      {/* Coloured zone arcs */}
      {ZONES.map((z, i) => (
        <path key={i}
          d={arc(valToAngle(z.from), valToAngle(z.to), 150)}
          stroke={z.color} strokeWidth="22" fill="none" strokeLinecap="butt"
          opacity="0.92" />
      ))}

      {/* Boundary tick labels */}
      <text {...polar(valToAngle(244), 192)} fill="#B7E9F2" fontSize="9" fontFamily="JetBrains Mono" textAnchor="middle">244</text>
      <text {...polar(valToAngle(480), 192)} fill="#22C5E5" fontSize="9" fontFamily="JetBrains Mono" textAnchor="middle">480</text>
      <text {...polar(valToAngle(600), 192)} fill="#00FF66" fontSize="9" fontFamily="JetBrains Mono" textAnchor="middle">600</text>
      <text {...polar(valToAngle(700), 192)} fill="#FFCC00" fontSize="9" fontFamily="JetBrains Mono" textAnchor="middle">700</text>
      <text {...polar(valToAngle(1000), 192)} fill="#FF3333" fontSize="9" fontFamily="JetBrains Mono" textAnchor="middle">1000</text>

      <text x="200" y="40" fill={COLORS.textDim} fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">SEC kWh/t</text>

      {/* FIXED baseline target line */}
      <line
        x1={polar(baseAngle, 125).x} y1={polar(baseAngle, 125).y}
        x2={polar(baseAngle, 175).x} y2={polar(baseAngle, 175).y}
        stroke="#00E5FF" strokeWidth="3"
      />
      <text {...polar(baseAngle, 112)} fill="#00E5FF" fontSize="10" fontFamily="Rajdhani" fontWeight="700" textAnchor="middle">BASELINE</text>
      <text {...polar(baseAngle, 100)} fill="#00E5FF" fontSize="11" fontFamily="JetBrains Mono" textAnchor="middle">{num(baseline, 0)}</text>

      {/* Needle */}
      <line x1="200" y1="200" x2={needle.x} y2={needle.y} stroke={color} strokeWidth="4" strokeLinecap="round" />
      <circle cx="200" cy="200" r="10" fill={color} />
      <circle cx="200" cy="200" r="14" fill="none" stroke={color} strokeOpacity="0.4" strokeWidth="2" />
    </svg>
  );
}

// Compact zone legend rendered under the gauge
function GaugeLegend() {
  const items = [
    { color: "#B7E9F2", label: "Theoretical 244 — aspirational" },
    { color: "#22C5E5", label: "480–540  World best practice" },
    { color: "#00FF66", label: "600–700  Good Indian foundry" },
    { color: "#FFCC00", label: "700–1000  Drift" },
    { color: "#FF3333", label: "> 1000  Poor — urgent" },
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-5 pb-4 -mt-2" data-testid="gauge-legend">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#8F93A2] font-display">
          <span className="inline-block w-3 h-2" style={{ background: it.color }} />
          <span>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

function SummaryCard({ title, value, sub, color, testid, icon: Icon }) {
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-small">{title}</span>
        {Icon && <Icon size={14} className="text-[#8F93A2]" />}
      </div>
      <div className="metric-mid mt-2" style={{ color: color || COLORS.text }} data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-xs text-[#8F93A2] mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function AlertsFeed({ alerts }) {
  if (!alerts?.length) {
    return (
      <div className="panel">
        <div className="panel-header"><span>Active Alerts</span><span className="badge-status green">0 ACTIVE</span></div>
        <div className="p-6 text-center text-sm text-[#8F93A2]" data-testid="alerts-empty">
          No active alerts. Plant operating within parameters.
        </div>
      </div>
    );
  }
  return (
    <div className="panel" data-testid="alerts-feed">
      <div className="panel-header">
        <span>Active Alerts (Ranked by ₹/hour)</span>
        <span className="badge-status red">{alerts.length} ACTIVE</span>
      </div>
      <div className="divide-y divide-[#1F212A] max-h-[340px] overflow-y-auto scrollbar-thin">
        {alerts.slice(0, 6).map((a) => (
          <div key={a.id} className="p-3 flex items-start gap-3" data-testid={`alert-${a.id}`}>
            <div className={`dot ${a.severity === "critical" ? "red" : "yellow"} mt-1.5`} />
            <div className="flex-1">
              <div className="font-display uppercase tracking-wider text-sm font-semibold">{a.name}</div>
              <div className="text-xs text-[#8F93A2] mt-0.5">{a.action}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm text-[#FF3333]">{inrFull(a.impact_per_hour_inr)}</div>
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">/hour</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CommandCentre() {
  const { state } = usePlant();
  const [snapshots, setSnapshots] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/plant/snapshots?hours=2");
        if (alive) setSnapshots(r.data.snapshots || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) {
    return <div className="p-10 text-center text-[#8F93A2]">Loading plant telemetry…</div>;
  }

  const m = state.metrics || {};
  const ai = state.ai || {};
  const m4 = ai.model4_rca || { causes: [] };
  const m1 = ai.model1_sec || {};
  const topCause = m4.causes?.[0] || null;
  const status = state.kpi_status?.sec || "green";
  const baseline = state.plant_params?.baseline_sec_target || 620;
  const actualSec = m.ce_07 || 0;
  const deviationVsBaseline = ((actualSec - baseline) / baseline) * 100;
  const absDelta = actualSec - baseline;

  // Power chart: use F1+F2 active kw from snapshots
  const powerData = snapshots.map((s) => ({
    t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" }),
    F1: s.tags?.FURN1_ACTIVE_KW || 0,
    F2: s.tags?.FURN2_ACTIVE_KW || 0,
  }));

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Command Centre</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Live Plant Intelligence</h1>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Furnace Efficiency Gauge — dominant */}
        <div className="col-span-12 lg:col-span-7 panel">
          <div className="panel-header">
            <span>Furnace Efficiency Gauge — Heartbeat of the Plant</span>
            <span className={`badge-status ${status}`}>{status.toUpperCase()}</span>
          </div>
          <div className="p-5 grid grid-cols-3 gap-4 items-center">
            <div className="col-span-2 h-[260px]">
              <EfficiencyGauge
                actual={actualSec || 720}
                theoretical={m.ce_04 || 244}
                baseline={baseline}
                threshold={850}
                status={status}
              />
            </div>            <div>
              <div className="label-small">SEC Actual</div>
              <div className="metric-large" style={{ color: statusColor(status) }} data-testid="cc-sec-actual">{num(actualSec, 0)}</div>
              <div className="text-xs text-[#8F93A2] mb-3">kWh per tonne melted</div>

              <div className="label-small mt-3">vs Plant Baseline</div>
              <div className="flex items-baseline gap-2">
                <div className="metric-mid text-[#00E5FF]" data-testid="cc-sec-baseline">{num(baseline, 0)}</div>
                <div className={`font-mono text-sm ${absDelta > 0 ? "text-[#FF3333]" : "text-[#00FF66]"}`} data-testid="cc-sec-delta">
                  {absDelta > 0 ? "+" : ""}{num(absDelta, 0)} kWh/t · {num(deviationVsBaseline, 1)}%
                </div>
              </div>
              <div className="text-xs text-[#8F93A2] mt-1">Fixed reference — anomaly isolated</div>

              <div className="label-small mt-3">Theoretical Min</div>
              <div className="metric-mid text-[#8F93A2]">{num(m.ce_04, 0)}<span className="text-sm ml-1">kWh/t</span></div>
              <div className="text-xs text-[#8F93A2] mt-1">Physics floor (grey iron)</div>
            </div>
          </div>
          <GaugeLegend />
        </div>

        {/* AI Insight Card */}
        <div className="col-span-12 lg:col-span-5 panel" data-testid="ai-insight-card">
          <div className="panel-header">
            <span><Sparkles className="inline mr-1" size={13} /> AI Insight — Model 4 RCA</span>
            <span className={`badge-status ${m4.deviation_active ? "red" : "green"}`}>
              {m4.deviation_active ? "Deviation Active" : "Within Parameters"}
            </span>
          </div>
          <div className="p-5">
            {topCause ? (
              <>
                <div className="label-small">Top Ranked Cause</div>
                <div className="text-base mt-1 font-display uppercase tracking-wide font-semibold" data-testid="ai-top-cause">{topCause.cause}</div>
                <div className="flex items-center gap-4 mt-3">
                  <div>
                    <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Probability</div>
                    <div className="font-mono text-2xl text-[#00E5FF]">{num(topCause.probability, 0)}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider">Cost this shift</div>
                    <div className="font-mono text-2xl text-[#FF3333]">{inrFull(topCause.impact_inr_per_shift)}</div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-[#14151C] border-l-2 border-[#00E5FF]">
                  <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider mb-1">Recommended action — {topCause.action_role}</div>
                  <div className="text-sm">{topCause.action}</div>
                </div>
              </>
            ) : (
              <div className="text-sm text-[#8F93A2]">Plant operating within expected parameters.</div>
            )}
            <div className="mt-4 pt-3 border-t border-[#1F212A]">
              <div className="text-[10px] text-[#8F93A2] uppercase tracking-wider mb-2">AI Narrative</div>
              <div className="text-sm text-[#FFFFFF]/90 leading-relaxed font-body" data-testid="ai-narrative">{state.narrative}</div>
            </div>
          </div>
        </div>

        {/* Today Summary cards */}
        <div className="col-span-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard
            title="Heats Today"
            value={state.shift?.heats_completed || 0}
            sub={`of ${state.shift?.planned_heats || 21} planned (Shift ${state.shift?.name || "A"})`}
            color={COLORS.text}
            testid="card-heats"
            icon={Flame}
          />
          <SummaryCard
            title="EBITDA Leakage"
            value={inrFull(m.ce_31 / 26)}
            sub="₹/day at current pace"
            color={m.ce_31 > 0 ? COLORS.red : COLORS.green}
            testid="card-ebitda"
          />
          <SummaryCard
            title="CQS Last Heat"
            value={num(m.ce_26, 0)}
            sub={(state.shift?.last_heat_summary?.cqs || 75) > 75 ? "Good" : (state.shift?.last_heat_summary?.cqs || 75) > 60 ? "Watch" : "Poor"}
            color={(state.shift?.last_heat_summary?.cqs || 75) > 75 ? COLORS.green : (state.shift?.last_heat_summary?.cqs || 75) > 60 ? COLORS.yellow : COLORS.red}
            testid="card-cqs"
          />
          <SummaryCard
            title="DISA Rate"
            value={num(state.tags?.DISA_MOULD_RATE, 0)}
            sub={state.tags?.DISA_RUNNING_STATUS > 0 ? "running" : "stopped"}
            color={state.tags?.DISA_RUNNING_STATUS > 0 ? COLORS.green : COLORS.red}
            testid="card-disa"
            icon={Activity}
          />
          <SummaryCard
            title="CO₂ Today"
            value={`${num((state.shift?.kwh_total || 0) * 0.82 / 1000, 2)} t`}
            sub={`${num(m.ce_28, 1)} t this month`}
            color={COLORS.text}
            testid="card-carbon"
          />
          <SummaryCard
            title="Active Alerts"
            value={(state.alerts || []).length}
            sub="click feed below"
            color={(state.alerts || []).length > 0 ? COLORS.red : COLORS.green}
            testid="card-alerts"
            icon={AlertCircle}
          />
        </div>

        {/* Power curve */}
        <div className="col-span-12 lg:col-span-7 panel">
          <div className="panel-header">
            <span>Live Furnace Power — last 2 hours</span>
            <span className="text-[10px] font-mono text-[#8F93A2]">Rated 550 kW · Hold 192 kW</span>
          </div>
          <div className="p-3 h-[280px]" data-testid="power-curve-chart">
            <ResponsiveContainer>
              <LineChart data={powerData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} tickLine={false} />
                <YAxis stroke={COLORS.textDim} fontSize={10} tickLine={false} domain={[0, 700]} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A", borderRadius: 0 }} labelStyle={{ color: COLORS.textDim }} />
                <ReferenceLine y={550} stroke={COLORS.green} strokeDasharray="3 3" label={{ value: "Rated", position: "right", fill: COLORS.green, fontSize: 10 }} />
                <ReferenceLine y={192} stroke={COLORS.red} strokeDasharray="3 3" label={{ value: "Holding", position: "right", fill: COLORS.red, fontSize: 10 }} />
                <Line type="monotone" dataKey="F1" stroke={COLORS.blue} dot={false} strokeWidth={2}  isAnimationActive={false} />
                <Line type="monotone" dataKey="F2" stroke={COLORS.amber} dot={false} strokeWidth={2}  isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alerts feed */}
        <div className="col-span-12 lg:col-span-5">
          <AlertsFeed alerts={state.alerts || []} />
        </div>
      </div>
    </div>
  );
}
